// ** React Imports
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

// ** MUI Imports
import {
  Button,
  DialogContent,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography
} from '@mui/material'
import { CatalogueDataType, CatalogueCategoryType } from 'src/lib/types/common/catalogue'
import SearchAsYouType from './subcomponents/SearchAsYouType'
import { getCatalogue } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { catalogueAdapter } from 'src/lib/utils/adapters'
import {
  filterCatalogueItem,
  findCatalogueItem,
  findCatalogueItemIndex,
  getIndeterminateStatus,
  getSelectionStatus,
  matchCatalogueItemId,
  numberWithCommas
} from 'src/lib/utils/helpers'
import Loader from 'src/components/common/loader-components/Loader'
import { alertSeverites } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Box } from 'react-feather'
import { Search, ChevronDown } from 'react-feather'
import { SearchParameterOptionsType } from 'src/lib/types/random'
import { errorToast } from 'src/lib/utils/toasters'

const searchParameterOptions: SearchParameterOptionsType[] = [
  {
    value: 'name_like',
    label: 'Name',
    placeholder: 'Enter product name'
  },
  {
    value: 'sku_like',
    label: 'SKU',
    placeholder: 'Enter SKU ID'
  }
]

const rowsPerPage = 10

interface CatalogueSearchProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  selectedData: CatalogueDataType[] | undefined | null
  setCatalogueData?: Dispatch<SetStateAction<CatalogueDataType[] | undefined | null>>
  catalogueCatagory: CatalogueCategoryType | undefined
  searchedItemType: string | undefined // Product or collection
  checkForInventory?: boolean
  saveCatalogueData?: (coveredCatalogueData: CatalogueDataType[] | undefined | null) => void
  showAllItemsOption?: boolean
  dialogTitle?: string
  selectionRange?: {
    min: number
    max: number
  }
  showClearButton?: boolean
  shouldClearLocalSelection?: boolean
  includeBundleProducts?: boolean
}

const CatalogueSearch: React.FC<CatalogueSearchProps> = ({
  isOpen,
  setIsOpen,
  setCatalogueData,
  selectedData,
  catalogueCatagory,
  searchedItemType,
  checkForInventory = false,
  saveCatalogueData,
  showAllItemsOption,
  dialogTitle,
  selectionRange,
  showClearButton,
  shouldClearLocalSelection = false,
  includeBundleProducts = false
}) => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()

  const accountId = merchant?.accountId
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [offSet, setOffset] = useState<number>(1)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [searchResults, setSearchResults] = useState<CatalogueDataType[] | null>([])
  const [totalItemCount, setTotalItemCount] = useState<number>(0)
  const [lastScrollPosition, setLastPosition] = useState<number>(0)
  const [searchedText, setSearchedText] = useState<string>('')
  const [localSelection, setLocalSelection] = useState<CatalogueDataType[] | undefined | null>([])
  const [allItemsSelected, setAllItemsSelected] = useState<boolean>(
    Boolean(selectedData?.[0]?.id === 'shopflo-all-products' || selectedData?.[0]?.id === 'shopflo-all-collections')
  )
  const [selectedSearchOption, setSelectedSearchOption] = useState<SearchParameterOptionsType>(
    searchParameterOptions[0]
  )
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const parameterMenuOpen = Boolean(anchorEl)

  const isVariantSelectionEnabled =
    Boolean(catalogueCatagory === 'PRODUCT_AND_VARIANT') || Boolean(catalogueCatagory === 'VARIANT')
  const isProductSelectionDisabled = Boolean(catalogueCatagory === 'VARIANT')

  const getTotalItemCount = (catalogueData: CatalogueDataType[] | undefined | null): number => {
    if (!catalogueData || catalogueData.length === 0) return 0
    return catalogueData.length
  }

  useEffect(() => {
    if (!merchant || !searchedItemType) return
    callGetCatalogueData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, searchedText, searchedItemType, selectedSearchOption])

  useEffect(() => {
    Boolean(selectedData?.length) && setLocalSelection(selectedData)
    shouldClearLocalSelection && setLocalSelection(selectedData)
  }, [selectedData])

  useEffect(() => {
    if (offSet !== 1) callGetCatalogueData(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offSet])

  // For maintaining the position where load more button was pressed
  useEffect(() => {
    if (!Boolean(searchResults?.length)) return
    const contentList = document.getElementById('scrollable-dialog-content')
    contentList?.scrollTo({
      top: lastScrollPosition,
      behavior: 'smooth'
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults])

  // Function to search catalogue. (Product / Collection)
  const callGetCatalogueData = async (loadMore = false) => {
    const currentOffset = loadMore ? offSet : 1
    const searchType = searchedItemType?.toLocaleLowerCase()
    try {
      setShowLoader(true)
      const catalogueResponse: any = await getCatalogue(
        issuerId,
        accountId,
        currentOffset,
        searchedText,
        searchType,
        selectedSearchOption.value,
        rowsPerPage,
        includeBundleProducts
      )

      // When there is an error in fetching the items.
      if (catalogueResponse?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, catalogueResponse.error)
        console.error('x-trace-id:', catalogueResponse.headers?.['x-trace-id'])
        return
      }

      // Adapter for catalogue items.
      const rows: CatalogueDataType[] = catalogueResponse?.data?.data?.map((row: any) => {
        const rowData = catalogueAdapter(row, searchedItemType)
        return rowData
      })
      setShowLoader(false)
      setTotalItemCount(catalogueResponse?.data?.total_count)

      // When loadMore button is pressed the new search results get appended to the existing results.
      if (loadMore && searchResults !== null) {
        setSearchResults([...searchResults, ...rows])
        return
      }
      setSearchResults(rows)
    } catch (e) {
      console.error(e)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('alert_item_load_failed'))
    }
  }

  // To handle variant selection in case of "PRODUCT_AND_VARIANT" catalogueCatagory.
  const handleVariantSelection = (listItem: CatalogueDataType) => {
    const parentElementIndex = findCatalogueItemIndex(localSelection, listItem?.parentDetails)

    // If parent of the clicked variant is checked or indeterminate.
    if (parentElementIndex !== -1) {
      const isVariantAlreadySelected = matchCatalogueItemId(
        localSelection?.[parentElementIndex ?? 0]?.variants,
        listItem
      )

      // if clicked variant is already selected.
      if (isVariantAlreadySelected) {
        const selectedVariantCount = localSelection?.[parentElementIndex ?? 0]?.variants?.length

        // if clicked variant is the last selected child of the parent.
        if (selectedVariantCount === 1) {
          const filteredCatalogueData = filterCatalogueItem(localSelection, listItem?.parentDetails)
          setLocalSelection([...(filteredCatalogueData ?? [])])
        } else {
          // if some other variants of the parent are also selected along with the clicked variant.
          const modifiedVariants = filterCatalogueItem(localSelection?.[parentElementIndex ?? 0]?.variants, listItem)
          const parentElement = {
            ...localSelection?.[parentElementIndex ?? 0],
            variants: modifiedVariants,
            allVariantsSelected: false
          }
          const modifiedSelection = [...(localSelection ?? [])]
          modifiedSelection[parentElementIndex ?? 0] = parentElement
          setLocalSelection([...modifiedSelection])
        }
      } else {
        // if clicked variant is not selected.
        const alreadySelectedVariants = localSelection?.[parentElementIndex ?? 0]?.variants

        if (typeof alreadySelectedVariants !== 'undefined') {
          const modifiedVariants = [...alreadySelectedVariants, listItem]
          const parentElement = { ...localSelection?.[parentElementIndex ?? 0], variants: modifiedVariants }

          // updating allVariantsSelected to differentiate for all\partial variants selection.
          if (modifiedVariants?.length === findCatalogueItem(searchResults, parentElement)?.variants?.length) {
            parentElement.allVariantsSelected = true
          }

          const modifiedSelection = [...(localSelection ?? [])]
          modifiedSelection[parentElementIndex ?? 0] = parentElement
          setLocalSelection([...modifiedSelection])
        }
      }
    } else {
      // If parent of the clicked variant is nor checked and neither indeterminate.
      const parentElement = findCatalogueItem(searchResults, listItem?.parentDetails)
      if (typeof parentElement !== 'undefined') {
        // If the variant is the only child of the parent.
        if (findCatalogueItem(searchResults, parentElement)?.variants?.length === 1) {
          parentElement.allVariantsSelected = true
        }
        const modifiedParent = { ...parentElement, variants: [listItem] }
        setLocalSelection([...(localSelection ?? []), modifiedParent])
      }
    }
  }

  // Item Selection Logics
  const handleSelection = (listItem: CatalogueDataType) => {
    const isItemAlreadySelected = matchCatalogueItemId(localSelection, listItem)
    switch (catalogueCatagory) {
      case 'PRODUCT_AND_VARIANT':
        if (!Boolean(listItem?.isVariant)) {
          if (isItemAlreadySelected) {
            setLocalSelection([...(filterCatalogueItem(localSelection, listItem) ?? [])])
            return
          }
          const modifiedListItem = { ...listItem, allVariantsSelected: true }
          setLocalSelection([...(localSelection ?? []), modifiedListItem])
          return
        }
        handleVariantSelection(listItem)
        break

      default:
        if (isItemAlreadySelected) {
          setLocalSelection([...(filterCatalogueItem(localSelection, listItem) ?? [])])
          return
        }
        setLocalSelection([...(localSelection ?? []), listItem])
    }
  }

  const handleCatalogueSearchClose = () => {
    if (shouldClearLocalSelection) setLocalSelection(selectedData)
    setIsOpen(false)
    setSearchedText('')
  }

  const handleCatalogueSelectionSave = async () => {
    if (allItemsSelected) {
      const allProductsPlaceholder: CatalogueDataType =
        catalogueCatagory === 'COLLECTION'
          ? { isVariant: false, id: 'shopflo-all-collections', description: 'All collections' }
          : { isVariant: true, id: 'shopflo-all-products', description: 'All products' }
      setCatalogueData && setCatalogueData([allProductsPlaceholder])
      if (typeof saveCatalogueData !== 'undefined') saveCatalogueData([allProductsPlaceholder])
      handleCatalogueSearchClose()
      return
    }
    if (Boolean(localSelection && selectionRange && localSelection?.length < selectionRange?.min)) {
      errorToast(`Select at least ${selectionRange?.min} items`)
      return
    }
    const allProductsPresentindex = localSelection?.findIndex(
      item => item?.id === 'shopflo-all-products' || item?.id === 'shopflo-all-collections'
    )
    const modifiedLocalSelection = [...(localSelection ?? [])]
    if (allProductsPresentindex !== undefined && Boolean(allProductsPresentindex > -1)) {
      modifiedLocalSelection?.splice(allProductsPresentindex, 1)
      setLocalSelection(modifiedLocalSelection)
    }
    setCatalogueData && setCatalogueData(modifiedLocalSelection)
    if (typeof saveCatalogueData !== 'undefined') saveCatalogueData(modifiedLocalSelection)
    handleCatalogueSearchClose()
  }

  const renderSearchFilters = () => {
    return (
      <div className='mr-2 -ml-2'>
        <div
          className='flex items-center py-2 pl-3 pr-2 space-x-1 cursor-pointer bg-gray-lighter'
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          <Search className='w-4 h-4' />
          <Typography className='font-medium whitespace-nowrap' variant='subtitle2'>
            by {selectedSearchOption.label}
          </Typography>
          <ChevronDown className='w-4 h-4' />
        </div>
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={parameterMenuOpen}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': 'basic-button'
          }}
        >
          {searchParameterOptions?.map((parameter: any, index: number) => {
            return (
              <MenuItem
                key={parameter?.value}
                onClick={() => {
                  setSelectedSearchOption(parameter)
                  setAnchorEl(null)
                }}
              >
                {parameter?.label}
              </MenuItem>
            )
          })}
        </Menu>
      </div>
    )
  }

  const renderCatalogueSearch = () => {
    return (
      <div className='flex flex-col'>
        <SearchAsYouType
          setSearchedText={setSearchedText}
          placeholder={
            catalogueCatagory === 'COLLECTION'
              ? `Enter ${searchedItemType?.toLocaleLowerCase()} name`
              : selectedSearchOption?.placeholder
          }
          autoFocus={true}
          hideBrowseButton={true}
          startAdornment={catalogueCatagory !== 'COLLECTION' ? renderSearchFilters() : <></>}
        />
        {Boolean(searchResults?.length) ? (
          <div id='scrollable-dialog-content' className='overflow-y-auto max-h-[60vh]'>
            <DialogContent
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 0
              }}
            >
            <List sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }} component='nav'>
              {showAllItemsOption && !Boolean(searchedText) && (
                <div className='rounded-lg shadow-sm'>
                  <ListItemButton
                    sx={{
                      padding: '6px 16px',
                      width: '100%',
                      border: '0.5px solid #E0E0E0',
                      borderRadius: '6px',
                      borderBottomWidth: '1px'
                    }}
                    onClick={() => setAllItemsSelected(!allItemsSelected)}
                  >
                    <ListItemIcon>
                      <FloCheckbox value={allItemsSelected} />
                    </ListItemIcon>
                    <ListItemAvatar sx={{ borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Avatar alt={searchedItemType} sx={{ height: 36, width: 36, borderRadius: '5px' }}>
                        <Box className='text-gray-400' />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      id='all-items'
                      primary={`${catalogueCatagory === 'COLLECTION' ? 'All Collections' : 'All Products'}`}
                      primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, color: 'text.primary' } }}
                    />
                  </ListItemButton>
                </div>
              )}
              {!allItemsSelected &&
                searchResults?.map((listItem: CatalogueDataType, index: number) => {
                  const showVariants = Boolean(listItem?.variants?.length) && isVariantSelectionEnabled
                  return (
                    <div key={`${listItem?.id}_${index}`} className='rounded-lg shadow-sm'>
                      <ListItemButton
                        sx={{
                          padding: '6px 16px',
                          width: '100%',
                          border: '0.5px solid #E0E0E0',
                          borderTopLeftRadius: '6px',
                          borderTopRightRadius: '6px',
                          borderBottomLeftRadius: showVariants ? '0px' : '6px',
                          borderBottomRightRadius: showVariants ? '0px' : '6px',
                          borderBottomWidth: showVariants ? '0px' : '1px'
                        }}
                        onClick={() => {
                          const currentCount = getTotalItemCount(localSelection)
                          if (
                            Boolean(selectionRange && currentCount >= selectionRange?.max) &&
                            !Boolean(getSelectionStatus(listItem, localSelection, catalogueCatagory))
                          )
                            return
                          if (isProductSelectionDisabled) return
                          handleSelection(listItem)
                        }}
                      >
                        {!Boolean(catalogueCatagory === 'VARIANT') ? (
                          <ListItemIcon>
                            <FloCheckbox
                              isIndeterminate={Boolean(
                                getIndeterminateStatus(listItem, localSelection, catalogueCatagory)
                              )}
                              value={Boolean(getSelectionStatus(listItem, localSelection, catalogueCatagory))}
                              isDisabled={
                                Boolean(selectionRange && getTotalItemCount(localSelection) >= selectionRange?.max) &&
                                !Boolean(getSelectionStatus(listItem, localSelection, catalogueCatagory))
                              }
                              tooltipTitle={
                                Boolean(selectionRange && getTotalItemCount(localSelection) >= selectionRange?.max) &&
                                !Boolean(getSelectionStatus(listItem, localSelection, catalogueCatagory))
                                  ? `${selectionRange?.max}/${selectionRange?.max} ${searchedItemType?.toLowerCase() ?? 'items'} selected`
                                  : undefined
                              }
                            />
                          </ListItemIcon>
                        ) : (
                          <></>
                        )}
                        <ListItemAvatar sx={{ borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                          <Avatar
                            alt={searchedItemType}
                            src={listItem?.imageUrl}
                            sx={{ height: 36, width: 36, borderRadius: '5px' }}
                          >
                            <Box className='text-gray-400' />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          id={listItem?.id}
                          primary={listItem?.description}
                          primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, color: 'text.primary' } }}
                        />
                      </ListItemButton>
                      {Boolean(showVariants) ? (
                        listItem?.variants?.map((childItem: CatalogueDataType, index: number) => {
                          const isAvailable = (childItem?.inventory ?? 0) > 0
                          const isLastElement = index + 1 === listItem?.variants?.length
                          const parentIsIndeterminate = Boolean(
                            getIndeterminateStatus(listItem, localSelection, catalogueCatagory)
                          )
                          const isVariantSelected = Boolean(
                            getSelectionStatus(childItem, localSelection, catalogueCatagory)
                          )
                          const currentCount = getTotalItemCount(localSelection)
                          const isCountAtLimit = Boolean(selectionRange && currentCount >= selectionRange?.max)
                          const shouldDisableVariant = isCountAtLimit && !isVariantSelected && !parentIsIndeterminate

                          return (
                            <div key={`${childItem?.id}_${index}`}>
                              <ListItemButton
                                disabled={!Boolean(isAvailable) && Boolean(checkForInventory)}
                                sx={{
                                  width: '100%',
                                  border: '0.5px solid #E0E0E0',
                                  borderRadius: '6px',
                                  borderTopLeftRadius: '0px',
                                  borderTopRightRadius: '0px',
                                  borderBottomWidth: isLastElement ? '1px' : '0px',
                                  borderBottomLeftRadius: isLastElement ? '6px' : '0px',
                                  borderBottomRightRadius: isLastElement ? '6px' : '0px',
                                  padding: '6px 1rem 6px 2rem'
                                }}
                                onClick={() => {
                                  if (shouldDisableVariant) return
                                  handleSelection(childItem)
                                }}
                              >
                                <ListItemIcon>
                                  <FloCheckbox
                                    value={isVariantSelected}
                                    isDisabled={shouldDisableVariant}
                                    tooltipTitle={
                                      shouldDisableVariant && selectionRange?.max
                                        ? `${selectionRange.max}/${selectionRange.max} ${searchedItemType?.toLowerCase() ?? 'items'} selected`
                                        : undefined
                                    }
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  id={childItem?.id}
                                  primary={
                                    <div
                                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    >
                                      <span className='w-1/2 overflow-hidden text-ellipsis whitespace-nowrap text-coal-dark'>
                                        {childItem?.description}
                                      </span>
                                      <div className='flex items-center justify-between w-1/2 overflow-hidden text-ellipsis whitespace-nowrap text-coal-dark'>
                                        <span className='flex justify-end w-1/2'>{childItem?.inventory} available</span>
                                        <span>₹ {numberWithCommas((childItem?.price ?? 0).toString())}</span>
                                      </div>
                                    </div>
                                  }
                                  primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500 } }}
                                />
                              </ListItemButton>
                            </div>
                          )
                        })
                      ) : (
                        <></>
                      )}
                    </div>
                  )
                })}
            </List>
            <div>
              {!allItemsSelected && totalItemCount > offSet ? (
                <Button
                  id='load-more-button'
                  onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                    setOffset(oldValues => oldValues + rowsPerPage)
                    const scrollingDiv = document?.getElementById('scrollable-dialog-content')
                    if (scrollingDiv) setLastPosition(event.currentTarget.offsetTop - scrollingDiv.offsetTop)
                  }}
                >
                  Load More +
                </Button>
              ) : (
                <></>
              )}
              {showClearButton ? (
                <Button
                  sx={{ padding: '4px 8px', color: `${localSelection?.length ? '' : '#6E6E6E'}` }}
                  disabled={!localSelection?.length}
                  onClick={() => setLocalSelection(null)}
                >
                  Clear
                </Button>
              ) : (
                <></>
              )}
            </div>
          </DialogContent>
          </div>
        ) : !Boolean(showLoader) ? (
          <Typography variant='body1' sx={{ padding: '1rem 1rem 0rem', fontWeight: 500 }}>
            {`You don't have any matching ${searchedItemType?.toLocaleLowerCase()}(s).`}
          </Typography>
        ) : (
          <></>
        )}
        {showLoader ? (
          <div className='mt-8'>
            <Loader />
          </div>
        ) : (
          <></>
        )}
      </div>
    )
  }

  // ** Alert Handlers ** //
  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }
  const handleCloseAlert = (_event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <FloGenericDialog
        isOpen={isOpen}
        width={600}
        handleClose={handleCatalogueSearchClose}
        handleJob={handleCatalogueSelectionSave}
        jobButtonText={'Save'}
        closeButtonText={'Cancel'}
        content={<>{renderCatalogueSearch()}</>}
        title={dialogTitle && Boolean(dialogTitle) ? dialogTitle : `Add ${searchedItemType?.toLocaleLowerCase()}`}
        footerTxt={
          selectionRange?.max && localSelection && Boolean(selectionRange?.max) ? (
            <div className='flex flex-row gap-2'>
              <span className='text-coal-dark font-medium text-sm'>{`${localSelection?.length} / ${selectionRange?.max} products selected`}</span>
            </div>
          ) : (
            <></>
          )
        }
      />

      {/* Alerts */}
      {openAlert?.length ? (
        <Alerts
          open={Boolean(openAlert?.length)}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default CatalogueSearch
